/**
 * OrderAccessError — the diner-facing card shown when an order can't be
 * loaded. Shared by DineInOrderScreen and SplitPayScreen so both surfaces
 * speak the same language and neither leaks a raw backend string.
 *
 * `kind` comes from `classifyOrderError` (see lib/order-error):
 *   - "identity"  → 401: we have no accepted identity. Offer sign-in (only
 *                   useful when there's no table QR context to fall back on).
 *   - "not_yours" → 404: the order isn't on this account. NO sign-in CTA —
 *                   re-authenticating as the same customer changes nothing.
 *   - "generic"   → retryable.
 */
import { useTranslation } from "react-i18next";

import { Button, Card } from "@/components/ui";
import type { OrderErrorKind } from "@/lib/order-error";

export interface OrderAccessErrorProps {
  kind: OrderErrorKind;
  /** Opens the global sign-in modal. Rendered for "identity" only. */
  onSignIn?: () => void;
  /** Retry the fetch. Rendered for "generic" only. */
  onRetry?: () => void;
  /** Leave the screen. Always rendered. */
  onBack: () => void;
}

export function OrderAccessError({
  kind,
  onSignIn,
  onRetry,
  onBack,
}: OrderAccessErrorProps) {
  const { t } = useTranslation();

  const title =
    kind === "identity"
      ? t("v2.order_error.identity_title")
      : kind === "not_yours"
        ? t("v2.order_error.not_yours_title")
        : t("v2.order_detail.couldnt_load");

  const body =
    kind === "identity"
      ? t("v2.order_error.identity_body")
      : kind === "not_yours"
        ? t("v2.order_error.not_yours_body")
        : t("v2.order_error.generic_body");

  return (
    <div role="alert">
    <Card padding={20} style={{ margin: 16, textAlign: "center" }}>
      <div
        className="display"
        style={{ fontWeight: 700, fontSize: 15, marginBottom: 6, color: "var(--ink-1)" }}
      >
        {title}
      </div>
      <div style={{ fontSize: 13, color: "var(--ink-2)", marginBottom: 14, lineHeight: 1.5 }}>
        {body}
      </div>
      {kind === "not_yours" && (
        <div style={{ fontSize: 12, color: "var(--ink-3)", marginBottom: 14 }}>
          {t("v2.order_error.scan_qr")}
        </div>
      )}
      <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
        <Button variant="outline" size="sm" onClick={onBack}>
          {t("v2.common.back")}
        </Button>
        {kind === "identity" && onSignIn && (
          <Button variant="primary" size="sm" onClick={onSignIn}>
            {t("v2.order_error.sign_in")}
          </Button>
        )}
        {kind === "generic" && onRetry && (
          <Button variant="primary" size="sm" onClick={onRetry}>
            {t("v2.common.retry")}
          </Button>
        )}
      </div>
    </Card>
    </div>
  );
}
